import React from 'react'
import { Button, Grid, IconButton, Paper, TableBody, TableCell, Link as MuiLink, TableContainer, TableHead, TableRow, Typography, useTheme, CircularProgress, DialogContent, DialogActions, Dialog, DialogTitle } from "@material-ui/core";
import { useFilters, useGlobalFilter, useSortBy, useTable } from "react-table";
import CallSplitIcon from "@material-ui/icons/CallSplit";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import MuiTable from "@material-ui/core/Table";
import { generalFilter, useDefaultColumn } from '../../util/filter';
import moment from 'moment';

export const FlashDetailsTable = ({ data, refetch }: any) => {
    const defaultColumn = useDefaultColumn();
    const theme = useTheme();
    
    const columns :any = React.useMemo(
        () => [
            {
                Header: "ID",
                accessor: "id",
                filter: generalFilter,
            },
            {
                id: "Serial Number",
                accessor: "serial_number",
                filter: generalFilter,
                width: 250,
                minWidth: 200,
                maxWidth: 300,
                align: "center",
                Cell: ({ value }: any) => <>{value ? value : "N/A"}</>
            },
            {
                id: "Date Time",
                accessor: "date_time",
                filter: generalFilter,
                width: 250,
                minWidth: 200,
                maxWidth: 300,
                align: "center",
                 Cell: ({ value }: any) => <>{value ? moment(value).format("DD-MM-YYYY HH:mm:ss") : "N/A"}</>
            },
              {
                id: "Json  file",
                accessor: "json_file_path",
                filter: generalFilter,
                width: 250,
                minWidth: 200,
                maxWidth: 300,
                align: "center",
                Cell: ({ value }: any) => {
                    const [loading, setLoading] = React.useState(false);
                    const handleDownload = async () => {
                        setLoading(true);
                        try {
                            const response = await fetch(value, { method: "GET" });
                            if (!response.ok) throw new Error("Failed to download file");
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            const filename = value.split("/").pop() || "downloaded_file";
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            window.URL.revokeObjectURL(url);
                        } catch (error) {
                            console.error("Download error:", error);
                            alert("File could not be downloaded.");
                        } finally {
                            setLoading(false);
                        }
                    };
                    return value ? (
                        <button
                            onClick={handleDownload}
                            style={{
                                color: "#3f51b5",
                                background: "none",
                                border: "none",
                                textDecoration: "underline",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                            }}
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="spinner" style={{ fontSize: "12px" }}>
                                    <CircularProgress size={20} />
                                </span>
                            ) : (
                                "View Report"
                            )}
                        </button>
                    ) : (
                        "N/A"
                    );
                },
            },
            
             {
                id: "PDF file",
                accessor: "pdf_file_path",
                filter: generalFilter,
                width: 250,
                minWidth: 200,
                maxWidth: 300,
                align: "center",
                Cell: ({ value, row }: any) => {
                    const [loading, setLoading] = React.useState(false);
                    const [open, setOpen] = React.useState(false);
                    const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
                    const [fileName, setFileName] = React.useState<string>("");

                    const handleView = async () => {
                        setLoading(true);
                        try {
                            const response = await fetch(value, { method: "GET" });
                            if (!response.ok) throw new Error("Failed to fetch file");

                            const blob = await response.blob();

                            // ✅ Use the filename from API response
                            const filename = row.original.pdf_file || "downloaded_file.pdf";
                            setFileName(filename);

                            // Create PDF blob URL
                            const url = window.URL.createObjectURL(
                                new Blob([blob], { type: "application/pdf" })
                            );
                            setPdfUrl(url);
                            setOpen(true);
                        } catch (error) {
                            console.error("View error:", error);
                            alert("PDF could not be opened.");
                        } finally {
                            setLoading(false);
                        }
                    };

                    const handleDownload = () => {
                        if (pdfUrl && fileName) {
                            const a = document.createElement("a");
                            a.href = pdfUrl;
                            a.download = fileName; 
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        }
                    };

                    return value ? (
                        <>
                            <button
                                onClick={handleView}
                                style={{
                                    color: "#3f51b5",
                                    background: "none",
                                    border: "none",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                }}
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={20} /> : "View PDF"}
                            </button>

                            {/* Dialog for PDF viewer */}
                            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
                                <DialogTitle>{fileName}</DialogTitle>
                                <DialogContent style={{ height: "80vh", padding: 0 }}>
                                    {pdfUrl && (
                                        <iframe
                                            src={pdfUrl}
                                            width="100%"
                                            height="100%"
                                            style={{ border: "none" }}
                                            title="PDF Preview"
                                        />
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleDownload} color="primary">
                                        Download
                                    </Button>
                                    <Button onClick={() => setOpen(false)} color="secondary">
                                        Close
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </>
                    ) : (
                        "N/A"
                    );
                },
            }
        ],
        []
    );
    const { getTableProps, visibleColumns, headerGroups, rows, prepareRow } =
        useTable(
            {
                data: data,
                columns,
                defaultColumn,
                initialState: {
                    hiddenColumns: ["id"],
                },
            },
            useFilters,
            useGlobalFilter,
            useSortBy,
        );
    return (
        <TableContainer
            data-testid="tableContainer"
            style={theme.tableContainer}
            component={Paper}
        >
            <MuiTable stickyHeader {...getTableProps()} size="small">
                <TableHead>
                    {headerGroups.map((headerGroup) => (
                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <TableCell
                                    style={{
                                        // ...theme.noLeftRightPadding,
                                        width: column.width,
                                        minWidth: column.minWidth,
                                        maxWidth: column.maxWidth,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Grid
                                        container
                                        direction="row"
                                        alignItems="center"
                                        spacing={0}
                                    >
                                        <Grid item xs={12} {...column.getHeaderProps()}>
                                            <Grid
                                                container
                                                direction="row"
                                                alignItems="center"
                                                spacing={0}
                                            >
                                                <Typography variant="subtitle2">
                                                    {(
                                                        column.id === "notes" &&
                                                        column.render("Header"))}
                                                </Typography>
                                                {column.canFilter ? column.render("Filter") : null}
                                                {/* <Typography variant="subtitle2">
                                                    {column.render("Header")}
                                                </Typography>
                                                {!(column.id === "notes" || column.id === "add_tasks") && column.canFilter && column.render("Filter")} */}
                                                <Grid item xs={2}>
                                                    <IconButton
                                                        type="submit"
                                                        aria-label="search"
                                                        size="small"
                                                        {...column.getSortByToggleProps()}
                                                    >
                                                        {column.isSorted ? (
                                                            column.isSortedDesc ? (
                                                                <ArrowDropDownIcon />
                                                            ) : (
                                                                <ArrowDropUpIcon />
                                                            )
                                                        ) : (
                                                            column.id !== "notes" && <CallSplitIcon />
                                                        )}
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        prepareRow(row);
                        return (
                            <>
                                <TableRow {...row.getRowProps()}>
                                    {row.cells.map((cell) => {
                                        return (
                                            <TableCell {...cell.getCellProps()}
                                                style={{
                                                    width: cell.column.width,
                                                    minWidth: cell.column.minWidth,
                                                    maxWidth: cell.column.maxWidth,
                                                    // textAlign: 'center',
                                                }}
                                            >
                                                {cell.render("Cell")}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </>
                        );
                    })}
                </TableBody>
            </MuiTable>
        </TableContainer>
    )
}